import React from 'react';
import { bool } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureCurrentUser, userDisplayNameAsString } from '../../util/data';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import {
  LayoutSingleColumn,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  LayoutWrapperFooter,
  Footer,
  Page,
} from '../../components';
import { TopbarContainer } from '../../containers';
import { ContactForm } from '../../forms';
import config from '../../config';

import css from './ContactPage.module.css';
import ContactDetails from './ContactDetails';
import { sendEmail, sendEmailSuccess } from './ContactPage.duck';
import { notification } from '../../util/notification';

export const ContactPageComponent = props => {
  const {
    currentUser,
    scrollingDisabled,
    sendEmailInProgress,
    sendEmailError,
    sendEmailSuccess,
    onSendEmail,
    intl,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const currentEmail = user.attributes.email || '';
  const displayName = userDisplayNameAsString(user);

  const title = intl.formatMessage(
    { id: 'ContactPage.schemaTitle' },
    {
      siteTitle: config.siteTitle,
    }
  );
  const description = intl.formatMessage({ id: 'ContactPage.schemaDescription' });
  const keywords = intl.formatMessage({ id: 'ContactPage.schemaKeywords' });

  return (
    <Page
      title={title}
      description={description}
      keywords={keywords}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="ContactPage" desktopClassName={css.desktopTopbar} />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.content}>
            <div className={css.titleContainer}>
              <h1 className={css.title}>
                <FormattedMessage id="ContactPage.title" />
              </h1>
              <h3 className={css.subTitle}>
                <FormattedMessage id="ContactPage.subTitle" />
              </h3>
            </div>
            <div className={css.formContainer}>
              <ContactDetails />
              <ContactForm
                className={css.contactForm}
                initialValues={{ name: displayName, email: currentEmail }}
                inProgress={sendEmailInProgress}
                sendEmailError={sendEmailError}
                sendEmailSuccess={sendEmailSuccess}
                // Send email anad restart the form on success
                onSubmit={(values, form) =>
                  onSendEmail(values)
                    .then(() => {
                      form.restart();
                      notification.success(
                        intl.formatMessage({ id: 'ContactPage.successMessage' })
                      );
                    })
                    .catch(() => {
                      form.restart();
                      notification.error(intl.formatMessage({ id: 'ContactPage.errorMessage' }));
                    })
                }
              />
            </div>
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer currentPage="ContactPage" />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

ContactPageComponent.defaultProps = {
  currentUser: null,
  sendEmailInProgress: false,
  sendEmailError: null,
  sendEmailSuccess: false,
};

ContactPageComponent.propTypes = {
  currentUser: propTypes.currentUser,
  sendEmailInProgress: bool.isRequired,
  sendEmailError: propTypes.error,
  sendEmailSuccess: bool.isRequired,

  // from connect
  scrollingDisabled: bool.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { sendEmailInProgress, sendEmailError, sendEmailSuccess } = state.ContactPage;
  return {
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
    sendEmailInProgress,
    sendEmailError,
    sendEmailSuccess,
  };
};

const mapDispatchToProps = dispatch => ({
  onSendEmail: params => dispatch(sendEmail(params)),
});

const ContactPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(ContactPageComponent);

export default ContactPage;
